import React from 'react';
import { useDataContext } from '../../context/data.js';
import translations from '../../constants/translations.json';

// StampProgressCard — the shared "klippekort" visual (§9.2) used by the item
// modal and the checkout overview. A punch-card look: gradient card, stamp
// slots (check = already earned, plus = earned by THIS order, dashed = empty)
// and a gift slot at the end that lights up when the order fills the card.
//
// NOTE ON COLORS: the Tailwind `primary` color is a CSS variable, so
// alpha-slash utilities like bg-primary/10 silently render as nothing.
// Everything here uses explicit hex from the brand palette (#96c11f family).
const StampProgressCard = ({
  name,
  current = 0, // stamps already on the card (this fill cycle)
  after = 0, // stamps after this order (current + potential, capped)
  maxCount = 0, // stamps required to fill the card
  potential = 0, // stamps this order adds
  capNote = null, // e.g. "max 1 pr. ordre" — null when uncapped
  completes = false, // this order fills the card
  className = '',
}) => {
  const { language } = useDataContext();
  const t = (key, fallback) => translations[language]?.[key] || fallback;

  if (!maxCount || maxCount <= 0) return null;

  const slots = Math.min(maxCount, 10);

  return (
    <div
      className={`rounded-xl border border-[#dcebb4] bg-gradient-to-br from-[#f6fae9] to-[#e9f3d2] px-3.5 py-3 shadow-sm ${className}`}
    >
      {/* Header: icon + campaign name + progress pill */}
      <div className="flex items-center gap-2.5">
        <div className="w-9 h-9 rounded-full bg-[#96c11f] flex items-center justify-center shrink-0 shadow-sm">
          <i className="fa fa-star text-white text-sm" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-xs sm:text-sm font-bold text-[#3f6212] truncate">{name}</p>
          <p className="text-[10px] sm:text-[11px] text-[#7a8f52]">
            {t('stamp_card', 'Klippekort')}
          </p>
        </div>
        <span className="shrink-0 text-[11px] sm:text-xs font-bold text-[#3f6212] bg-white border border-[#dcebb4] rounded-full px-2.5 py-1 shadow-sm whitespace-nowrap">
          {current}/{maxCount}
          {after > current && (
            <>
              {' '}
              <span className="text-[#96c11f]">→ {after}/{maxCount}</span>
            </>
          )}
        </span>
      </div>

      {/* Stamp slots + gift slot */}
      <div className="flex items-center gap-1.5 mt-2.5 flex-wrap">
        {Array.from({ length: slots }).map((_, i) => {
          if (i < current) {
            // Already earned
            return (
              <span
                key={i}
                className="w-7 h-7 rounded-full bg-[#96c11f] border-2 border-[#96c11f] flex items-center justify-center shadow-sm"
              >
                <i className="fa fa-check text-white text-[10px]" />
              </span>
            );
          }
          if (i < after) {
            // Earned by THIS order
            return (
              <span
                key={i}
                className="w-7 h-7 rounded-full bg-white border-2 border-[#96c11f] flex items-center justify-center shadow-sm"
              >
                <i className="fa fa-plus text-[#96c11f] text-[10px]" />
              </span>
            );
          }
          // Still empty
          return (
            <span
              key={i}
              className="w-7 h-7 rounded-full bg-white/70 border-2 border-dashed border-[#c9d8a8]"
            />
          );
        })}
        {maxCount > slots && (
          <span className="text-[10px] text-[#7a8f52] font-semibold">+{maxCount - slots}</span>
        )}

        {/* The reward slot at the end of the card */}
        <span className="mx-0.5 text-[#c9d8a8]">
          <i className="fa fa-angle-right text-xs" />
        </span>
        <span
          className={`w-8 h-8 rounded-full flex items-center justify-center text-sm border-2 shadow-sm ${
            completes
              ? 'bg-amber-400 border-amber-400'
              : 'bg-white/70 border-dashed border-[#c9d8a8] opacity-60 grayscale'
          }`}
        >
          <span role="img" aria-label="reward">
            🎁
          </span>
        </span>
      </div>

      {/* Footer: what this order does */}
      <p className="mt-2 text-[11px] sm:text-xs text-[#557024]">
        {potential > 0 ? (
          <>
            <span className="font-bold text-[#3f6212]">
              +{potential} {t('stamp_one', 'klip')}
            </span>{' '}
            {t('stamp_with_purchase', 'ved dette køb')}
            {capNote ? <span className="text-[#7a8f52]"> ({capNote})</span> : null}
          </>
        ) : null}
      </p>
      {completes && (
        <p className="mt-1.5 text-[11px] sm:text-xs font-bold text-white bg-amber-500 rounded-lg px-2.5 py-1.5 inline-block shadow-sm">
          🎉 {t('stamp_completes_long', 'Kortet fyldes — din belønning er klar efter dette køb!')}
        </p>
      )}
    </div>
  );
};

export default StampProgressCard;
